import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Save, FileCheck2, FileX2, Trash2, Download, Plus, X, User, Car, Wrench, Banknote, HandCoins, Landmark, CreditCard, MoreHorizontal, Ban, BellRing, Loader2 } from "lucide-react";
import { INV_STATUS, PAY_STATUS } from "@/pages/Invoices";
import { Send, Mail } from "lucide-react";

const fmtEur = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";

function InvoiceSectionHeading({ number, icon: Icon, title }) {
  return (
    <div className="stellar-invoice-section-heading">
      <span className="stellar-invoice-section-step">{number}</span>
      <Icon className="h-4 w-4" />
      <h2 className="font-display text-base font-bold">{title}</h2>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [pickupPaymentMethod, setPickupPaymentMethod] = useState("");
  const [pickupPaymentDate, setPickupPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfObjectUrl, setPdfObjectUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const { data: inv } = useQuery({
    queryKey: ["invoice", id],
    queryFn: async () => (await api.get(`/invoices/${id}`)).data,
  });

  useEffect(() => {
    if (inv) {
      setLines(inv.lines || []);
      setNotes(inv.notes || "");
    }
  }, [inv]);

  useEffect(() => () => {
    if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
  }, [pdfObjectUrl]);

  const isDraft = inv?.status === "entwurf";

  const saveMut = useMutation({
    mutationFn: async () => (await api.put(`/invoices/${id}`, {
      customer_id: inv.customer_id,
      vehicle_id: inv.vehicle_id,
      job_id: inv.job_id,
      lines: lines.map(l => ({
        service_name: l.service_name,
        description: l.description || "",
        quantity: Number(l.quantity) || 0,
        unit_price: Number(l.unit_price) || 0,
        total: 0,
      })),
      notes,
    })).data,
    onSuccess: () => { toast.success("Gespeichert"); qc.invalidateQueries({ queryKey: ["invoice", id] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const issueMut = useMutation({
    mutationFn: async () => {
      const issued = (await api.post(`/invoices/${id}/issue`)).data;

      if (pickupPaymentMethod) {
        try {
          await api.post(`/invoices/${id}/payments`, {
            amount: Number(issued.total || 0),
            payment_date: pickupPaymentDate,
            payment_method: pickupPaymentMethod,
            reference: "",
            notes: "Bezahlt bei Fahrzeugabholung",
          });
        } catch (paymentError) {
          paymentError.invoiceWasIssued = true;
          paymentError.issuedInvoice = issued;
          throw paymentError;
        }
      }

      return issued;
    },
    onSuccess: async (d) => {
      qc.setQueryData(["invoice", id], d);
      toast.success(
        pickupPaymentMethod
          ? `Rechnung gestellt und Zahlung erfasst: ${d.invoice_number}`
          : `Rechnung gestellt: ${d.invoice_number}`
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["invoice", id] }),
        qc.invalidateQueries({ queryKey: ["invoices"] }),
        qc.invalidateQueries({ queryKey: ["payments", id] }),
        qc.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
    onError: async (e) => {
      if (e.invoiceWasIssued) {
        toast.error("Rechnung wurde gestellt, aber die Zahlung konnte nicht erfasst werden. Bitte Zahlung manuell erfassen.");
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["invoice", id] }),
          qc.invalidateQueries({ queryKey: ["invoices"] }),
          qc.invalidateQueries({ queryKey: ["payments", id] }),
          qc.invalidateQueries({ queryKey: ["dashboard"] }),
        ]);
        return;
      }
      toast.error(apiError(e));
    },
  });
  const cancelMut = useMutation({
    mutationFn: async () => (await api.post(`/invoices/${id}/cancel`)).data,
    onSuccess: () => { toast.success("Storniert"); qc.invalidateQueries(); },
  });

  // Sprint 26 — send invoice via email
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendSubject, setSendSubject] = useState("");
  const [sendBody, setSendBody] = useState("");
  const openSendDialog = () => {
    const defaultTo = (inv?.customer_snapshot?.email || inv?.billing_details?.email || "").trim();
    const invNo = inv?.invoice_number || "";
    const defaultSubject = `Rechnung ${invNo} – Stellar Aufbereitung`;
    const defaultBody = "Hallo,\n\nanbei erhalten Sie Ihre Rechnung als PDF.\n\nVielen Dank für Ihr Vertrauen.\n\nMit freundlichen Grüßen\nStellar Aufbereitung";
    setSendTo(defaultTo);
    setSendSubject(defaultSubject);
    setSendBody(defaultBody);
    setSendOpen(true);
  };
  const sendMut = useMutation({
    mutationFn: async () => (await api.post(`/invoices/${id}/send`, {
      to: sendTo.trim(),
      subject: sendSubject.trim(),
      body: sendBody.trim(),
    })).data,
    onSuccess: () => {
      toast.success(`Rechnung an ${sendTo} gesendet`);
      setSendOpen(false);
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderTo, setReminderTo] = useState("");
  const [reminderSubject, setReminderSubject] = useState("");
  const [reminderBody, setReminderBody] = useState("");
  const openReminderDialog = () => {
    const recipient = (inv?.sent_to || inv?.customer_snapshot?.email || inv?.billing_details?.email || "").trim();
    const name = inv?.customer_snapshot?.name || "";
    const greeting = name ? `Hallo ${name},` : "Guten Tag,";
    const due = inv?.due_date ? ` Die Rechnung war am ${fmtDate(inv.due_date)} fällig.` : "";
    setReminderTo(recipient);
    setReminderSubject(`Zahlungserinnerung zur Rechnung ${inv?.invoice_number || ""}`);
    setReminderBody(`${greeting}\n\nbei der Prüfung unserer Unterlagen ist uns aufgefallen, dass zur Rechnung ${inv?.invoice_number || ""} noch ein Betrag von ${fmtEur(inv?.balance_due)} offen ist.${due}\n\nFalls Sie den Betrag bereits überwiesen haben, betrachten Sie diese Nachricht bitte als gegenstandslos.\n\nVielen Dank.\n\nFreundliche Grüße\nAuto Stellar Aufbereitung`);
    setReminderOpen(true);
  };
  const reminderMut = useMutation({
    mutationFn: async () => (await api.post(`/invoices/${id}/send-reminder`, {
      to: reminderTo.trim(),
      subject: reminderSubject.trim(),
      body: reminderBody.trim(),
    })).data,
    onSuccess: () => {
      toast.success(`Zahlungserinnerung an ${reminderTo} gesendet`);
      setReminderOpen(false);
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["emails-sent"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/invoices/${id}`)).data,
    onSuccess: () => { toast.success("Entwurf gelöscht"); qc.invalidateQueries({ queryKey: ["invoices"] }); navigate("/rechnungen"); },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!inv) return <div className="p-8 text-muted-foreground">Laden…</div>;

  const st = INV_STATUS[inv.status] || INV_STATUS.entwurf;
  const pdfUrl = `${API}/invoices/${id}/pdf?auth=${encodeURIComponent(localStorage.getItem("stellar_token") || "")}`;
  const canRemind = ["gestellt", "gesendet"].includes(inv.status) && Number(inv.balance_due || 0) > 0.005;

  const addLine = () => setLines([...lines, { service_name: "", description: "", quantity: 1, unit_price: 0 }]);
  const updateLine = (idx, key, val) => {
    const cp = [...lines]; cp[idx] = { ...cp[idx], [key]: val }; setLines(cp);
  };
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const computedSubtotal = lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const closePdf = () => {
    setPdfOpen(false);
    if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
    setPdfObjectUrl("");
  };

  const openPdf = async () => {
    setPdfOpen(true);
    setPdfLoading(true);
    try {
      const response = await fetch(pdfUrl, { credentials: "same-origin" });
      if (!response.ok) throw new Error("PDF konnte nicht geladen werden.");
      const blob = await response.blob();
      if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
      setPdfObjectUrl(URL.createObjectURL(blob));
    } catch (e) {
      setPdfOpen(false);
      toast.error(e?.message || "PDF konnte nicht geladen werden.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="stellar-page stellar-detail-page">
      <Link to="/rechnungen" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-invoices">
        <ArrowLeft className="h-4 w-4" /> Rechnungen
      </Link>

      <div className="stellar-detail-heading stellar-detail-violet mb-5 flex-wrap max-sm:flex-col max-sm:items-stretch max-sm:gap-4">
        <div className="w-full min-w-0 flex-1 sm:w-auto">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${st.cls}`}>
              <st.Icon className="h-3 w-3" /> {st.label}
            </span>
            {inv.issued_at && <span className="text-[10px] text-muted-foreground">Gestellt am {fmtDate(inv.issued_at)}</span>}
            {inv.cancelled_at && <span className="text-[10px] text-destructive">Storniert am {fmtDate(inv.cancelled_at)}</span>}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight font-mono break-words">
            {isDraft ? "Rechnungsentwurf" : inv.invoice_number}
          </h1>
        </div>
      </div>

      {inv.sent_at && (
        <div className="mb-4 rounded-md border border-sky-500/30 bg-sky-500/5 p-3 text-xs text-sky-700 dark:text-sky-400 flex items-center gap-2" data-testid="sent-badge">
          <Mail className="h-3.5 w-3.5" />
          Zuletzt gesendet an <span className="font-mono">{inv.sent_to || "—"}</span> am {new Date(inv.sent_at).toLocaleString("de-DE")}
        </div>
      )}
      {inv.last_reminder_at && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700" data-testid="reminder-sent-badge">
          <BellRing className="h-3.5 w-3.5" />
          Letzte Zahlungserinnerung an <span className="font-mono">{inv.last_reminder_to || "—"}</span> am {new Date(inv.last_reminder_at).toLocaleString("de-DE")}
          {inv.reminder_count > 1 && <span>· insgesamt {inv.reminder_count}</span>}
        </div>
      )}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Rechnung {inv.invoice_number} per E-Mail versenden</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Empfänger *</Label>
              <Input
                type="email"
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
                placeholder="kunde@example.com"
                data-testid="send-invoice-to"
              />
              {!sendTo.trim() && (
                <p className="text-[11px] text-destructive mt-1">Empfänger-E-Mail-Adresse fehlt.</p>
              )}
            </div>
            <div>
              <Label>Betreff</Label>
              <Input
                value={sendSubject}
                onChange={(e) => setSendSubject(e.target.value)}
                data-testid="send-invoice-subject"
              />
            </div>
            <div>
              <Label>Nachricht</Label>
              <Textarea
                rows={7}
                value={sendBody}
                onChange={(e) => setSendBody(e.target.value)}
                data-testid="send-invoice-body"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">Die PDF wird automatisch angehängt (<span className="font-mono">{inv.invoice_number}.pdf</span>).</p>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSendOpen(false)} data-testid="send-invoice-cancel">Abbrechen</Button>
            <Button
              onClick={() => sendMut.mutate()}
              disabled={sendMut.isPending || !sendTo.trim() || !sendTo.includes("@")}
              data-testid="send-invoice-submit"
            >
              <Send className="h-4 w-4 mr-1" /> {sendMut.isPending ? "Senden…" : "Senden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Zahlungserinnerung prüfen</DialogTitle></DialogHeader>
          <div className="mt-2 space-y-3">
            <div><Label>Empfänger *</Label><Input type="email" value={reminderTo} onChange={(e) => setReminderTo(e.target.value)} data-testid="reminder-to" /></div>
            <div><Label>Betreff</Label><Input value={reminderSubject} onChange={(e) => setReminderSubject(e.target.value)} data-testid="reminder-subject" /></div>
            <div><Label>Nachricht</Label><Textarea rows={10} value={reminderBody} onChange={(e) => setReminderBody(e.target.value)} data-testid="reminder-body" /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setReminderOpen(false)}>Abbrechen</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => reminderMut.mutate()} disabled={reminderMut.isPending || !reminderTo.includes("@")} data-testid="send-payment-reminder">
              <BellRing className="mr-1 h-4 w-4" /> {reminderMut.isPending ? "Senden…" : "Erinnerung senden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pdfOpen} onOpenChange={(open) => { if (!open) closePdf(); }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-5xl h-[calc(100dvh-1rem)] sm:h-[90vh] p-3 sm:p-5 flex flex-col">
          <DialogHeader className="shrink-0 pr-8">
            <DialogTitle>Rechnung {inv.invoice_number || "Entwurf"}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 rounded-md border bg-muted/30 overflow-hidden">
            {pdfLoading && (
              <div className="h-full flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" /> PDF wird geladen…
              </div>
            )}
            {!pdfLoading && pdfObjectUrl && (
              <iframe title={`Rechnung ${inv.invoice_number || "Entwurf"}`} src={pdfObjectUrl} className="h-full w-full bg-white" />
            )}
          </div>
          <DialogFooter className="shrink-0 flex-row gap-2 justify-end">
            {pdfObjectUrl && (
              <a href={pdfObjectUrl} download={`${inv.invoice_number || "Entwurf"}.pdf`} className="inline-flex">
                <Button variant="outline" type="button"><Download className="h-4 w-4 mr-1" /> Herunterladen</Button>
              </a>
            )}
            <Button type="button" onClick={closePdf}>Zurück zur Rechnung</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isDraft && (
        <div className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
          Diese Rechnung ist gestellt und aus rechtlichen Gründen unveränderlich. Nur Stornierung ist möglich.
        </div>
      )}

      <section className="stellar-invoice-section">
        <InvoiceSectionHeading number="1" icon={FileCheck2} title="Rechnungsdaten" />
        <div className="premium-card stellar-invoice-overview-grid">
          <Link to={`/kunden/${inv.customer_id}`} className="stellar-invoice-overview-item hover:bg-slate-50">
            <User className="h-5 w-5 text-emerald-600" />
            <div className="min-w-0">
              <div className="stellar-eyebrow">Kunde</div>
              <div className="font-semibold truncate">{inv.customer_snapshot?.name || "—"}</div>
            </div>
          </Link>
          <div className="stellar-invoice-overview-item">
            <Car className="h-5 w-5 text-violet-600" />
            <div className="min-w-0">
              <div className="stellar-eyebrow">Fahrzeug</div>
              {inv.vehicle_id ? (
                <Link to={`/fahrzeuge/${inv.vehicle_id}`} className="font-semibold hover:underline break-words">{inv.vehicle_snapshot?.display || "—"}</Link>
              ) : <div className="font-semibold">—</div>}
            </div>
          </div>
          <div className="stellar-invoice-overview-item">
            <Wrench className="h-5 w-5 text-blue-600" />
            <div className="min-w-0">
              <div className="stellar-eyebrow">Auftrag</div>
              {inv.job_id ? <Link to={`/auftraege/${inv.job_id}`} className="font-semibold hover:underline">Auftrag öffnen</Link> : <div className="font-semibold">—</div>}
            </div>
          </div>
        </div>
      </section>

      <section className="stellar-invoice-section">
      <InvoiceSectionHeading number="2" icon={Wrench} title="Positionen & Gesamtbetrag" />
      <div className="premium-card overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center gap-2">
          <div className="font-display font-semibold">Positionen ({lines.length})</div>
          {isDraft && <Button size="sm" variant="ghost" className="ml-auto" onClick={addLine} data-testid="invoice-add-line"><Plus className="h-4 w-4 mr-1" /> Hinzufügen</Button>}
        </div>
        <div className="divide-y divide-border/60">
          {lines.length === 0 && <div className="p-4 text-sm text-muted-foreground">Keine Positionen.</div>}
          {lines.map((l, idx) => (
            <div key={idx} className="p-3 grid grid-cols-12 gap-2 items-start" data-testid={`invoice-line-${idx}`}>
              <div className="col-span-12 sm:col-span-6">
                <Input value={l.service_name} onChange={(e) => updateLine(idx, "service_name", e.target.value)} placeholder="Leistung" disabled={!isDraft} />
                <Input value={l.description || ""} onChange={(e) => updateLine(idx, "description", e.target.value)} placeholder="Beschreibung (optional)" className="mt-1 text-xs" disabled={!isDraft} />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <div className="text-[10px] text-muted-foreground sm:hidden mb-0.5">Menge</div>
                <Input type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(idx, "quantity", Number(e.target.value) || 0)} disabled={!isDraft} />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <div className="text-[10px] text-muted-foreground sm:hidden mb-0.5">Preis</div>
                <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(idx, "unit_price", Number(e.target.value) || 0)} disabled={!isDraft} />
              </div>
              <div className="col-span-4 sm:col-span-2 text-right font-medium mt-2">{fmtEur((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</div>
              {isDraft && (
                <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeLine(idx)}><X className="h-4 w-4 text-destructive" /></Button>
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-border/60 space-y-1 text-sm">
          <div className="flex justify-end gap-8">
            <span className="text-muted-foreground">Zwischensumme:</span>
            <span className="font-medium w-32 text-right">{fmtEur(isDraft ? computedSubtotal : inv.subtotal)}</span>
          </div>
          {inv.kleinunternehmer ? (
            <div className="text-xs text-muted-foreground text-right italic">Gemäß §19 UStG wird keine Umsatzsteuer berechnet.</div>
          ) : (
            <div className="flex justify-end gap-8">
              <span className="text-muted-foreground">USt {inv.tax_rate}%:</span>
              <span className="font-medium w-32 text-right">{fmtEur(inv.tax_amount)}</span>
            </div>
          )}
          <div className="flex justify-end gap-8 pt-2 border-t border-border/60">
            <span className="font-display font-semibold">Gesamt:</span>
            <span className="font-display font-bold text-xl w-32 text-right">{fmtEur(isDraft ? computedSubtotal : inv.total)}</span>
          </div>
        </div>
      </div>
      </section>

      <section className="stellar-invoice-section">
      <InvoiceSectionHeading number="3" icon={Mail} title="Rechnungsnotiz" />
      <div className="premium-card p-4" title="Diese Notiz erscheint auf der Rechnung und im PDF.">
        <Label>Kundenseitige Notiz</Label>
        <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!isDraft} data-testid="invoice-notes" placeholder="Optional. Erscheint auf der Rechnung / PDF." />
      </div>
      </section>

      {isDraft && (
        <section className="stellar-invoice-section">
          <InvoiceSectionHeading number="4" icon={Banknote} title="Zahlung bei Fahrzeugabholung" />
          <div className="premium-card p-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Optional. Nur auswählen, wenn die Rechnung bereits bei Fahrzeugabholung vollständig bezahlt wurde.
            </p>

            <RadioGroup
              value={pickupPaymentMethod}
              onValueChange={setPickupPaymentMethod}
              className="grid gap-3 sm:grid-cols-3"
            >
              <Label className="flex cursor-pointer items-center gap-3 rounded-xl border p-3">
                <RadioGroupItem value="bar" />
                <HandCoins className="h-4 w-4" />
                Bar bezahlt
              </Label>
              <Label className="flex cursor-pointer items-center gap-3 rounded-xl border p-3">
                <RadioGroupItem value="ueberweisung" />
                <Landmark className="h-4 w-4" />
                Per Überweisung bezahlt
              </Label>
              <Label className="flex cursor-pointer items-center gap-3 rounded-xl border p-3">
                <RadioGroupItem value="paypal" />
                <CreditCard className="h-4 w-4" />
                Per PayPal bezahlt
              </Label>
            </RadioGroup>

            {pickupPaymentMethod && (
              <div className="flex flex-wrap items-end gap-3">
                <div className="max-w-xs">
                  <Label>Zahlungsdatum</Label>
                  <Input
                    type="date"
                    value={pickupPaymentDate}
                    onChange={(e) => setPickupPaymentDate(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setPickupPaymentMethod("")}
                >
                  Auswahl löschen
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {!isDraft && (
        <section className="stellar-invoice-section">
          <InvoiceSectionHeading number="4" icon={Banknote} title="Zahlungen" />
          <PaymentsSection invoice={inv} />
        </section>
      )}

      <section className="stellar-invoice-section">
      <InvoiceSectionHeading number="5" icon={MoreHorizontal} title="Aktionen" />
      <div className="premium-card stellar-invoice-actions flex flex-wrap gap-3 p-4">
        {isDraft && (
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="save-invoice"><Save className="h-4 w-4 mr-1" /> Speichern</Button>
        )}
        <Button variant="outline" type="button" onClick={openPdf} data-testid="download-pdf">
          <Download className="h-4 w-4 mr-1" /> PDF öffnen
        </Button>
        {isDraft && (
          <Button className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100" variant="outline" onClick={() => issueMut.mutate()} disabled={lines.length === 0 || (pickupPaymentMethod && !pickupPaymentDate)} data-testid="issue-invoice">
            <FileCheck2 className="h-4 w-4 mr-1" /> Rechnung stellen
          </Button>
        )}
        {(inv.status === "gestellt" || inv.status === "gesendet") && (
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={openSendDialog} data-testid="send-invoice-open">
            <Send className="h-4 w-4 mr-1" /> {inv.status === "gesendet" ? "Erneut senden" : "Rechnung senden"}
          </Button>
        )}
        {canRemind && (
          <Button variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" onClick={openReminderDialog} data-testid="open-payment-reminder">
            <BellRing className="h-4 w-4 mr-1" /> Zahlung erinnern
          </Button>
        )}
        {(inv.status === "gestellt" || inv.status === "gesendet") && (
          <Button variant="outline" onClick={() => { if (confirm("Rechnung stornieren? Kann nicht rückgängig gemacht werden.")) cancelMut.mutate(); }} data-testid="cancel-invoice">
            <FileX2 className="h-4 w-4 mr-1" /> Stornieren
          </Button>
        )}
        {isDraft && (
          <Button variant="ghost" className="text-destructive ml-auto" onClick={() => { if (confirm("Entwurf löschen?")) deleteMut.mutate(); }} data-testid="delete-invoice"><Trash2 className="h-4 w-4 mr-1" /> Löschen</Button>
        )}
      </div>
      </section>
    </div>
  );
}

const PAY_METHOD_META = {
  bar:          { label: "Bar",         Icon: HandCoins },
  ueberweisung: { label: "Überweisung", Icon: Landmark },
  paypal:       { label: "PayPal",       Icon: CreditCard },
  karte:        { label: "Karte",       Icon: CreditCard },
  sonstige:     { label: "Sonstige",    Icon: MoreHorizontal },
};

function PaymentsSection({ invoice }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: payments = [] } = useQuery({
    queryKey: ["payments", invoice.id],
    queryFn: async () => (await api.get(`/invoices/${invoice.id}/payments`)).data,
  });
  const cancelPaymentMut = useMutation({
    mutationFn: async (pid) => (await api.post(`/payments/${pid}/cancel`)).data,
    onSuccess: () => {
      toast.success("Zahlung storniert");
      qc.invalidateQueries({ queryKey: ["payments", invoice.id] });
      qc.invalidateQueries({ queryKey: ["invoice", invoice.id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const ps = PAY_STATUS[invoice.payment_status] || PAY_STATUS.offen;
  const remaining = Math.max(0, invoice.balance_due || 0);
  const overpayment = Math.max(0, Number(invoice.paid_total || 0) - Number(invoice.total || 0));
  const canPay = ["gestellt", "gesendet"].includes(invoice.status) && remaining > 0.005;

  return (
      <div className="premium-card overflow-hidden" data-testid="payments-section">
      <div className="p-4 border-b border-border/60 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Banknote className="h-5 w-5 text-primary" />
          <div className="font-display font-semibold">Zahlungen</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${ps.cls}`} data-testid="invoice-payment-status">
          {ps.label}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-4 text-sm">
          <div>
            <span className="text-muted-foreground text-xs mr-1">Bezahlt:</span>
            <span className="font-medium">{fmtEur(invoice.paid_total)}</span>
          </div>
          {overpayment > 0.005 ? (
            <div>
              <span className="text-muted-foreground text-xs mr-1">Überzahlung:</span>
              <span className="font-semibold text-amber-700 dark:text-amber-400" data-testid="invoice-overpayment">
                {fmtEur(overpayment)}
              </span>
            </div>
          ) : (
            <div>
              <span className="text-muted-foreground text-xs mr-1">Offen:</span>
              <span className={`font-semibold ${remaining > 0.005 ? "text-amber-700 dark:text-amber-400" : "text-emerald-700 dark:text-emerald-400"}`} data-testid="invoice-balance-due">
                {fmtEur(remaining)}
              </span>
            </div>
          )}
          {canPay && (
            <Button size="sm" onClick={() => setOpen(true)} data-testid="register-payment-btn">
              <Plus className="h-4 w-4 mr-1" /> Zahlung erfassen
            </Button>
          )}
        </div>
      </div>

      <div className="divide-y divide-border/60">
        {payments.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Noch keine Zahlungen erfasst.</div>
        ) : payments.map((p) => {
          const m = PAY_METHOD_META[p.payment_method] || PAY_METHOD_META.sonstige;
          const isCancelled = p.status === "storniert";
          return (
            <div key={p.id} className={`p-3 flex items-center gap-3 ${isCancelled ? "bg-slate-50 opacity-70" : ""}`} data-testid={`payment-item-${p.id}`}>
              <m.Icon className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${isCancelled ? "line-through" : ""}`}>{fmtEur(p.amount)} · <span className="text-muted-foreground font-normal">{m.label}</span></div>
                <div className="text-xs text-muted-foreground">
                  {fmtDate(p.payment_date)}
                  {p.reference && ` · Beleg: ${p.reference}`}
                  {p.notes && ` · ${p.notes}`}
                  {isCancelled && ` · Storniert${p.cancelled_at ? ` am ${fmtDate(p.cancelled_at)}` : ""}`}
                </div>
              </div>
              {!isCancelled && (
                <Button size="sm" variant="ghost" className="text-amber-700" onClick={() => { if (confirm("Zahlung stornieren? Sie bleibt im Verlauf erhalten und der offene Rechnungsbetrag wird neu berechnet.")) cancelPaymentMut.mutate(p.id); }} data-testid={`cancel-payment-${p.id}`}>
                  <Ban className="h-4 w-4 mr-1" /> Stornieren
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <PaymentDialog
        open={open}
        onOpenChange={setOpen}
        invoiceId={invoice.id}
        suggestedAmount={remaining}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ["payments", invoice.id] });
          qc.invalidateQueries({ queryKey: ["invoice", invoice.id] });
          qc.invalidateQueries({ queryKey: ["invoices"] });
          qc.invalidateQueries({ queryKey: ["dashboard"] });
        }}
      />
    </div>
  );
}

function PaymentDialog({ open, onOpenChange, invoiceId, suggestedAmount, onSuccess }) {
  const [amount, setAmount] = useState(suggestedAmount || 0);
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("bar");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setAmount(suggestedAmount || 0);
      setPayDate(new Date().toISOString().slice(0, 10));
      setMethod("bar");
      setReference("");
      setNotes("");
    }
  }, [open, suggestedAmount]);

  const registerMut = useMutation({
    mutationFn: async () => (await api.post(`/invoices/${invoiceId}/payments`, {
      amount: Number(amount),
      payment_date: payDate,
      payment_method: method,
      reference,
      notes,
    })).data,
    onSuccess: () => {
      toast.success("Zahlung erfasst");
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Zahlung erfassen</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Betrag (€)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Datum</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="payment-date" />
            </div>
            <div>
              <Label>Zahlungsart</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="ueberweisung">Überweisung</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="karte">Karte</SelectItem>
                  <SelectItem value="sonstige">Sonstige</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Beleg / Referenz</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="z.B. Quittung Nr., Buchungsref." data-testid="payment-reference" />
          </div>
          <div>
            <Label>Anmerkungen</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="payment-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={() => registerMut.mutate()} disabled={registerMut.isPending || !amount || Number(amount) <= 0} data-testid="payment-submit">
            <Save className="h-4 w-4 mr-1" /> Erfassen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
